import * as turf from '@turf/turf';
import mapboxgl from 'mapbox-gl';

export function animateRoute(map, coordinates, options = {}) {
    const {
        lineId = 'animated-route-line',
        sourceId = 'animated-route-source',
        pointId = 'animated-point',
        pointSourceId = 'animated-point-source',
        color = '#000000',
        zoom = 5.3,
        pitch = 0,
        kmPerFrame = 10,
        animationDuration = 9000,
        onComplete,
        onProgress,
        distanceUnit,
        isExport = false,
        fps = 30
    } = options;

    console.log("STARTING NOW", isExport ? "(EXPORT MODE)" : "");

    // Validation
    if (!map || !coordinates || !Array.isArray(coordinates) || coordinates.length < 2) {
        console.warn('Invalid parameters for animateRoute');
        return { stop: () => {} };
    }

    let animationId = null;
    let isAnimating = false;

    // Clean up existing layers/sources
    const cleanupAnimation = () => {
        console.log("Clean");
        if (map.getLayer(lineId)) map.removeLayer(lineId);
        if (map.getLayer(pointId)) map.removeLayer(pointId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        if (map.getSource(pointSourceId)) map.removeSource(pointSourceId);
    };

    // Stop animation function
    const stopAnimation = (finalStop = false) => {
        console.log("Stop", animationId);
        if (animationId) {
            if (isExport) {
                clearTimeout(animationId);
            } else {
                cancelAnimationFrame(animationId);
            }
            animationId = null;
        }
        isAnimating = false;
        if (!finalStop) {
            cleanupAnimation();
        }
    };

    const arc = [];

    try {
        // 1. Generate smooth arc (same as before)
        const fullLine = turf.lineString(coordinates);
        const simplified = turf.simplify(fullLine, { tolerance: 0.1, highQuality: false });
        
        let processedLine;
        try {
            processedLine = turf.bezierSpline(simplified, { sharpness: 0.85 });
        } catch (error) {
            console.warn('Bezier spline failed, using simplified line:', error);
            processedLine = simplified;
        }

        const lineDistance = turf.length(processedLine, { units: 'kilometers' });
        let steps = Math.max(50, Math.floor(lineDistance / (kmPerFrame * 0.1)));

        if (isExport) {
            steps = Math.floor((animationDuration / 1000) * fps);
        }

        console.log("(should be below 10000, number of steps: ", steps)

        for (let i = 0; i <= steps; i++) {
            try {
                const segment = turf.along(processedLine, (lineDistance * i) / steps, { units: 'kilometers' });
                arc.push(segment.geometry.coordinates);
            } catch (error) {
                console.warn('Error generating arc point:', error);
                const ratio = i / steps;
                const startCoord = coordinates[0];
                const endCoord = coordinates[coordinates.length - 1];
                arc.push([
                    startCoord[0] + (endCoord[0] - startCoord[0]) * ratio,
                    startCoord[1] + (endCoord[1] - startCoord[1]) * ratio
                ]);
            }
        }

        // 2. Add route and point to map (same as before)
        map.addSource(sourceId, {
            type: 'geojson',
            data: {
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [arc[0]]
                }
            }
        });

        map.addLayer({
            id: lineId,
            type: 'line',
            source: sourceId,
            paint: {
                'line-color': color,
                'line-width': 4,
                'line-opacity': 0.8
            },
        });

        map.addSource(pointSourceId, {
            type: 'geojson',
            data: {
                type: 'Feature',
                geometry: { 
                    type: 'Point', 
                    coordinates: arc[0] 
                }
            }
        });

        map.addLayer({
            id: pointId,
            type: 'circle',
            source: pointSourceId,
            paint: {
                'circle-radius': 6,
                'circle-color': color,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff',
                'circle-opacity': 1
            },
        });
    } catch (error) {
        console.error('Error setting up animation:', error);
        return { stop: stopAnimation };
    }

    panCamera(map, arc, isExport);

    // 3. Start animation - different logic for export vs normal
    animate();

    let currentIndex = 0;
    function animate() {
        // Your original animation logic
        let startTime = null;

        function animate(timestamp) {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / animationDuration, 1);

            const targetIndex = Math.floor(progress * (arc.length - 1));

            if (targetIndex >= arc.length - 1) {
                panCamera(map, arc, isExport);
                stopAnimation(true);
                if (onComplete) onComplete();
                return;
            }

            const currentCoord = arc[targetIndex];

            // Update point
            const pointSource = map.getSource(pointSourceId);
            if (pointSource) {
                pointSource.setData({
                    type: 'Feature',
                    geometry: { 
                        type: 'Point', 
                        coordinates: currentCoord 
                    }
                });
            }

            // Update line
            const lineSource = map.getSource(sourceId);
            if (lineSource) {
                lineSource.setData({
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: arc.slice(0, targetIndex + 1),
                    }
                });
            }

            // Move camera
            if (targetIndex % 5 === 0) {
                map.easeTo({
                    center: currentCoord,
                    pitch,
                    bearing: 0,
                    zoom,
                    duration: 1000,
                    easing: t => t,
                });
            }

            // Progress callback
            if (onProgress) {
                onProgress({
                    progress: progress,
                    currentCoordinate: currentCoord,
                    index: targetIndex,
                    total: arc.length
                });
            }

            currentIndex = targetIndex;
            animationId = requestAnimationFrame(animate);
        }

        isAnimating = true;
        setTimeout(() => {
            animationId = requestAnimationFrame(animate);
        }, 1500);
    }

    // Return control object
    return {
        stop: stopAnimation,
        isAnimating: () => isAnimating,
        getProgress: () => currentIndex / (arc.length - 1),
        arc: arc
    };
}

function panCamera(map, arc, isExport) {
    const bounds = arc.reduce((b, coord) => {
        return b.extend(coord);
    }, new mapboxgl.LngLatBounds(arc[0], arc[0]));

    try {
        if (isExport) {
            // For export, use jumpTo for instant positioning
            const center = bounds.getCenter();
            map.jumpTo({
                center: [center.lng, center.lat],
                pitch: 0,
                bearing: 0,
                zoom: 3.5
            });
        } else {
            // For normal mode, use smooth animation
            map.fitBounds(bounds, {
                padding: { top: 0, bottom: 250, left: 0, right: 0 },
                duration: 1500,
                pitch: 0,
                bearing: 0,
                zoom: 3.5
            });
        }
    } catch (error) {
        console.warn('Error fitting bounds:', error);
    }
}